import type { TimeSlot } from "@/pages/human-resources/time-management/business/time-slot/TimeSlot.ts";
import {
  computeTagWorkDurationMinutes,
  type ScheduleDailyTag,
} from "@/pages/human-resources/time-management/business/schedule/ScheduleDailyTag.ts";
import type { MetaData } from "@/pages/human-resources/time-management/business/MetaData.ts";
import { immerable } from "immer";
import type { AchievedScheduleValidationLevel } from "@/pages/human-resources/time-management/business/schedule/AchievedScheduleValidationLevel.ts";
import type { Contract } from "@/pages/human-resources/time-management/business/contract/Contract.ts";

interface ScheduleMandatoryArgs {
  readonly siteId: string;
  readonly employeeId: string;
  readonly date: Date;
}

export const MAX_NB_MEALS = 2;

interface ScheduleOptionalArgs {
  readonly modified?: boolean;
  readonly id?: string;
  readonly publishedId?: string;
  readonly achievedValidationLevel?: AchievedScheduleValidationLevel;
  readonly otherSiteId?: string;
  readonly label?: string;
  readonly comment?: string;
  readonly nbMeals?: number;
  readonly color?: string;
  readonly timeSlots?: TimeSlot[];
  readonly dailyTags?: ScheduleDailyTag[];
  readonly valorizedWorkMinutes?: number;
  readonly metaData?: MetaData;
}

export class Schedule implements ScheduleMandatoryArgs, ScheduleOptionalArgs {
  [immerable] = true;

  public readonly siteId: string;
  public readonly employeeId: string;
  public readonly date: Date;

  public readonly id: string | undefined;
  /**
   * publishedId is defined *if and only if* this is a deleted draft.
   */
  public readonly publishedId: string | undefined;
  public readonly achievedValidationLevel:
    | AchievedScheduleValidationLevel
    | undefined;
  /**
   * modified is true if draft and published schedules are different.
   */
  public readonly modified: boolean;
  public readonly otherSiteId: string | undefined;
  public readonly label: string | undefined;
  public readonly comment: string | undefined;
  public readonly nbMeals: number;
  public readonly color: string | undefined;
  public readonly timeSlots: TimeSlot[];
  public readonly dailyTags: ScheduleDailyTag[];
  public readonly valorizedWorkMinutes: number;
  public readonly metaData?: MetaData | undefined;

  public constructor(
    mandatory: ScheduleMandatoryArgs,
    optionalArgs?: ScheduleOptionalArgs,
  ) {
    this.siteId = mandatory.siteId;
    this.employeeId = mandatory.employeeId;
    this.date = mandatory.date;
    this.id = optionalArgs?.id;
    this.publishedId = optionalArgs?.publishedId;
    this.achievedValidationLevel = optionalArgs?.achievedValidationLevel;
    this.modified = optionalArgs?.modified ?? false;
    this.otherSiteId = optionalArgs?.otherSiteId;
    this.label = optionalArgs?.label;
    this.comment = optionalArgs?.comment;
    this.nbMeals = optionalArgs?.nbMeals ?? 0;
    this.color = optionalArgs?.color;
    this.timeSlots = optionalArgs?.timeSlots ?? [];
    this.dailyTags = optionalArgs?.dailyTags ?? [];
    this.valorizedWorkMinutes = optionalArgs?.valorizedWorkMinutes ?? 0;
    this.metaData = optionalArgs?.metaData;
  }

  public hasNoScheduleData(): boolean {
    return this.timeSlots.length === 0 && this.dailyTags.length === 0;
  }

  public isInvalid(): boolean {
    return (
      this.timeSlots.some((ts) => ts.invalid()) ||
      this.timeSlots.some((ts) => ts.overlapsAnyOf(this.timeSlots))
    );
  }

  public canTemplatize(): boolean {
    return this.timeSlots.some((ts) => ts.canAppearInTemplate());
  }

  public hasWork(): boolean {
    return (
      this.timeSlots.some((ts) => ts.computeWorkDurationMinutes() > 0) ||
      this.dailyTags.some((dt) => dt.tag.isWork())
    );
  }

  public computeWorkDurationMinutes(contract: Contract | undefined): number {
    return (
      this.computeWorkDurationTimeSlots() +
      this.computeWorkDurationTags(contract)
    );
  }

  public computeWorkDurationTimeSlots(): number {
    return this.timeSlots
      .map((ts) => ts.computeWorkDurationMinutes())
      .reduce((a, b) => a + b, 0);
  }

  public computeWorkDurationTags(contract: Contract | undefined): number {
    return this.dailyTags
      .map((dt) => computeTagWorkDurationMinutes(dt, contract))
      .reduce((a, b) => a + b, 0);
  }

  public computeBreakDurationMinutes(): number {
    return this.timeSlots
      .map((ts) => ts.computeBreakDurationMinutes())
      .reduce((a, b) => a + b, 0);
  }

  public isDeletedDraft(): boolean {
    return this.publishedId !== undefined;
  }

  public hasDailySoloTag(): boolean {
    return this.dailyTags.map((dt) => dt.tag).some((tag) => tag.solo);
  }

  public static createEmpty(
    siteId: string,
    employeeId: string,
    date: Date,
  ): Schedule {
    return new Schedule({ siteId, employeeId, date });
  }
}
